import { forwardRef, Motion, system, motion, Icon, Button, Loader, List, ListItem, Body, Center, useControllableState, useBoolean, splitSystemProps, VisuallyHidden } from '@hover/blueprint';
import { useStyleConfig, PopoverAnchor, PopoverContent, PopoverBody, useFormControlContext, useMultiStyleConfig, Popover, Portal } from '@hover/blueprint/chakra';
import { createContext, useContext, useCallback, useId, forwardRef as forwardRef$1 } from 'react';
import { createContext as createContext$1 } from '@chakra-ui/react-utils';
import invariant from 'tiny-invariant';
import { jsx, jsxs } from '@emotion/react/jsx-runtime';
import { iX } from '@hover/icons';
import { AnimatePresence } from 'framer-motion';
import { useCombobox } from 'downshift';

function _toPrimitive(input, hint) {
  if (typeof input !== "object" || input === null) return input;
  var prim = input[Symbol.toPrimitive];
  if (prim !== undefined) {
    var res = prim.call(input, hint || "default");
    if (typeof res !== "object") return res;
    throw new TypeError("@@toPrimitive must return a primitive value.");
  }
  return (hint === "string" ? String : Number)(input);
}

function _toPropertyKey(arg) {
  var key = _toPrimitive(arg, "string");
  return typeof key === "symbol" ? key : String(key);
}

function _defineProperty(obj, key, value) {
  key = _toPropertyKey(key);
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  var _i = null == arr ? null : "undefined" != typeof Symbol && arr[Symbol.iterator] || arr["@@iterator"];
  if (null != _i) {
    var _s,
      _e,
      _x,
      _r,
      _arr = [],
      _n = !0,
      _d = !1;
    try {
      if (_x = (_i = _i.call(arr)).next, 0 === i) {
        if (Object(_i) !== _i) return;
        _n = !1;
      } else for (; !(_n = (_s = _x.call(_i)).done) && (_arr.push(_s.value), _arr.length !== i); _n = !0);
    } catch (err) {
      _d = !0, _e = err;
    } finally {
      try {
        if (!_n && null != _i.return && (_r = _i.return(), Object(_r) !== _r)) return;
      } finally {
        if (_d) throw _e;
      }
    }
    return _arr;
  }
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;
  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];
  return arr2;
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

var DropdownContext = /*#__PURE__*/createContext(undefined);
DropdownContext.displayName = 'DropdownContext';
var useDropdownContext = function useDropdownContext() {
  var context = useContext(DropdownContext);
  !context ? process.env.NODE_ENV !== "production" ? invariant(false, 'useDropdownContext returned `undefined`. Seems you forgot to wrap the components in `<DropdownProvider />`') : invariant(false) : void 0;
  return context;
};
var DropdownProvider = function DropdownProvider(_ref) {
  var value = _ref.value,
    children = _ref.children;
  var GenericProvider = DropdownContext.Provider;
  return jsx(GenericProvider, {
    value: value,
    children: children
  });
};
var _createContextUtil = /*#__PURE__*/createContext$1({
    name: "DropdownStylesContext",
    errorMessage: "useDropdownStyles returned is 'undefined'. Seems you forgot to wrap the components in \"<Dropdown />\" "
  }),
  _createContextUtil2 = /*#__PURE__*/_slicedToArray(_createContextUtil, 2),
  DropdownStylesProvider = _createContextUtil2[0],
  useDropdownStyles = _createContextUtil2[1];

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose(source, excluded);
  var key, i;
  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }
  return target;
}

var _excluded$3 = ["isOpen", "size"];
var CHEVRON_DOWN = '6 9 12 15 18 9';
var CHEVRON_UP = '18 15 12 9 6 15';
var DropdownIcon = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var isOpen = _ref.isOpen,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? 'small' : _ref$size,
    props = _objectWithoutProperties(_ref, _excluded$3);
  var styles = useStyleConfig('Icon', {
    size: size
  });
  return jsx(Motion.Firm, {
    children: jsx(system.svg, _objectSpread2(_objectSpread2({
      className: "feather feather-chevron-down",
      fill: "none",
      ref: ref,
      stroke: "currentColor",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      viewBox: "0 0 24 24",
      xmlns: "http://www.w3.org/2000/svg"
    }, props), {}, {
      __css: styles,
      children: jsx(motion.polyline, {
        animate: {
          points: isOpen ? CHEVRON_UP : CHEVRON_DOWN
        },
        points: CHEVRON_DOWN
      })
    }))
  });
});

var _excluded$2 = ["iconBefore"];
var MotionLoader = /*#__PURE__*/motion(Loader);
var DropdownInputSingle = function DropdownInputSingle(_ref) {
  var iconBefore = _ref.iconBefore,
    props = _objectWithoutProperties(_ref, _excluded$2);
  var styles = useDropdownStyles();
  var _useDropdownContext = useDropdownContext(),
    selectedItem = _useDropdownContext.selectedItem,
    itemToString = _useDropdownContext.itemToString,
    isOpen = _useDropdownContext.isOpen,
    getInputProps = _useDropdownContext.getInputProps,
    getClearButtonProps = _useDropdownContext.getClearButtonProps,
    getToggleButtonProps = _useDropdownContext.getToggleButtonProps,
    isLoading = _useDropdownContext.isLoading;
  return jsx(PopoverAnchor, {
    children: jsxs(system.div, _objectSpread2(_objectSpread2({}, props), {}, {
      __css: styles.field,
      children: [iconBefore && jsx(system.div, {
        __css: styles.icon,
        children: jsx(Icon, {
          icon: iconBefore
        })
      }), selectedItem && jsx(system.span, {
        __css: styles.selectedItem,
        noOfLines: 1,
        children: itemToString(selectedItem)
      }), jsx(system.input, _objectSpread2({
        __css: styles.input,
        display: selectedItem ? 'none' : 'block'
      }, getInputProps())), jsxs(system.div, {
        __css: styles.controls,
        children: [jsx(Motion.Firm, {
          children: jsx(AnimatePresence, {
            children: isLoading && jsx(MotionLoader, {
              animate: {
                opacity: 1
              },
              exit: {
                opacity: 0
              }
            })
          })
        }), selectedItem ? jsx(system.button, _objectSpread2(_objectSpread2({}, getClearButtonProps()), {}, {
          __css: styles.clearButton,
          children: jsx(Icon, {
            icon: iX
          })
        })) : jsx(Button, _objectSpread2(_objectSpread2({}, getToggleButtonProps()), {}, {
          fill: "minimal",
          shape: "square",
          sx: styles.toggleButton,
          children: jsx(DropdownIcon, {
            isOpen: isOpen
          })
        }))]
      })]
    }))
  });
};

var _excluded$1 = ["items"];
var MotionCenter = /*#__PURE__*/motion(Center);
var DropdownList = function DropdownList(_ref) {
  var items = _ref.items,
    props = _objectWithoutProperties(_ref, _excluded$1);
  var styles = useDropdownStyles();
  var _useDropdownContext = useDropdownContext(),
    itemToString = _useDropdownContext.itemToString,
    getMenuProps = _useDropdownContext.getMenuProps,
    itemToValue = _useDropdownContext.itemToValue,
    itemToKey = _useDropdownContext.itemToKey,
    getItemProps = _useDropdownContext.getItemProps,
    isLoading = _useDropdownContext.isLoading,
    emptyText = _useDropdownContext.emptyText;
  return jsx(PopoverContent, _objectSpread2(_objectSpread2({
    boxSizing: "border-box",
    overflow: "hidden",
    padding: "0",
    width: "100%"
  }, props), {}, {
    children: jsxs(PopoverBody, {
      position: "relative",
      sx: styles.body,
      children: [jsx(List, _objectSpread2(_objectSpread2({
        sx: styles.list
      }, getMenuProps()), {}, {
        children: items.map(function (item, index) {
          var _itemToKey;
          return jsxs(ListItem, _objectSpread2(_objectSpread2({
            alignItems: "center",
            display: "flex",
            flexDirection: "row",
            justifyContent: "flex-start",
            sx: styles.item
          }, getItemProps({
            item: item,
            index: index
          })), {}, {
            children: ['icon' in item && item.icon && jsx(Icon, {
              icon: item.icon,
              mr: "200"
            }), itemToString(item)]
          }), (_itemToKey = itemToKey === null || itemToKey === void 0 ? void 0 : itemToKey(item)) !== null && _itemToKey !== void 0 ? _itemToKey : itemToValue(item));
        })
      })), items.length === 0 && jsx(Body, {
        color: "neutral.500",
        m: "0",
        px: "200",
        children: emptyText !== null && emptyText !== void 0 ? emptyText : 'No results'
      }), jsx(Motion.Firm, {
        children: jsx(AnimatePresence, {
          children: isLoading && jsx(MotionCenter, {
            animate: {
              opacity: 1
            },
            bg: "lightAlpha.700",
            bottom: "0",
            exit: {
              opacity: 0
            },
            left: "0",
            position: "absolute",
            right: "0",
            top: "0"
          })
        })
      })]
    })
  }));
};

var _excluded = ["getInputProps", "getMenuProps", "getToggleButtonProps"];

/**
 * Props that are also accessible on `useDropdownContext`
 */

var useDropdown = function useDropdown(props) {
  var _control$isInvalid, _items$find;
  var defaultValue = props.defaultValue,
    emptyText = props.emptyText,
    _props$inPortal = props.inPortal,
    inPortal = _props$inPortal === void 0 ? false : _props$inPortal,
    isInvalidProp = props.isInvalid,
    isLoading = props.isLoading,
    itemToString = props.itemToString,
    itemToValue = props.itemToValue,
    items = props.items,
    name = props.name,
    onChange = props.onChange,
    placeholder = props.placeholder,
    value = props.value;
  var control = useFormControlContext();
  var labelIdProp = {
    'aria-labelledby': control === null || control === void 0 ? void 0 : control.getLabelProps().id
  };
  var isInvalid = (_control$isInvalid = control === null || control === void 0 ? void 0 : control.isInvalid) !== null && _control$isInvalid !== void 0 ? _control$isInvalid : isInvalidProp;
  var stateReducer = useCallback(function (state, _ref) {
    var changes = _ref.changes;
    return _objectSpread2(_objectSpread2({}, changes), {}, {
      inputValue:
      // We display the selected value in a non-editable element, so make
      // sure the input doesn't get populated at all when an item is selected
      // as it causes jank as the input is swapped for the selected element
      state.selectedItem || changes.selectedItem ? '' : changes.inputValue
    });
  }, []);
  var _useControllableState = useControllableState({
      value: value,
      defaultValue: defaultValue,
      onChange: onChange
    }),
    _useControllableState2 = _slicedToArray(_useControllableState, 2),
    internalValue = _useControllableState2[0],
    setInternalValue = _useControllableState2[1];
  var _useBoolean = useBoolean(false),
    _useBoolean2 = _slicedToArray(_useBoolean, 2),
    isInputFocused = _useBoolean2[0],
    _useBoolean2$ = _useBoolean2[1],
    onInputFocus = _useBoolean2$.on,
    onInputBlur = _useBoolean2$.off;
  var _useCombobox = useCombobox(_objectSpread2(_objectSpread2({}, props), {}, {
      id: useId(),
      onSelectedItemChange: function onSelectedItemChange(_ref2) {
        var item = _ref2.selectedItem;
        return setInternalValue(item ? itemToValue(item) : null);
      },
      stateReducer: stateReducer,
      selectedItem: (_items$find = items.find(function (item) {
        return itemToValue(item) === internalValue;
      })) !== null && _items$find !== void 0 ? _items$find : null
    })),
    _getInputProps = _useCombobox.getInputProps,
    _getMenuProps = _useCombobox.getMenuProps,
    _getToggleButtonProps = _useCombobox.getToggleButtonProps,
    comboboxProps = _objectWithoutProperties(_useCombobox, _excluded);
  var openMenu = comboboxProps.openMenu,
    isOpen = comboboxProps.isOpen;
  var getToggleButtonColorScheme = function getToggleButtonColorScheme() {
    if (isInvalid) return 'danger';
    if (isInputFocused) return 'primary';
    return 'neutral';
  };
  return _objectSpread2(_objectSpread2({
    emptyText: emptyText,
    isInputFocused: isInputFocused,
    isInvalid: isInvalid,
    isLoading: isLoading,
    inPortal: inPortal,
    itemToString: itemToString,
    itemToValue: itemToValue
  }, comboboxProps), {}, {
    getMenuProps: function getMenuProps(options, otherOptions) {
      return (
        // TODO: contribute better type definitions for `getMenuProps`
        // and other prop getters to upstream Downshift repository
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        _getMenuProps(_objectSpread2(_objectSpread2({}, labelIdProp), options), _objectSpread2({
          suppressRefError: inPortal
        }, otherOptions))
      );
    },
    getInputProps: function getInputProps(options, otherOptions) {
      return _objectSpread2(_objectSpread2({}, _getInputProps(_objectSpread2(_objectSpread2({
        onBlur: onInputBlur,
        onFocus: onInputFocus
      }, labelIdProp), options), otherOptions
      // TODO: contribute better type definitions for `getInputProps`
      // and other prop getters to upstream Downshift repository
      )), {}, {
        placeholder: placeholder
      });
    },
    getContainerProps: function getContainerProps() {
      return {
        'data-group': '',
        'data-focus': isInputFocused ? '' : undefined,
        'data-invalid': isInvalid ? '' : undefined
      };
    },
    getToggleButtonProps: function getToggleButtonProps() {
      return _objectSpread2(_objectSpread2({}, _getToggleButtonProps()), {}, {
        colorScheme: getToggleButtonColorScheme(),
        'aria-label': isOpen ? 'Close' : 'Open'
      });
    },
    getHiddenInputProps: function getHiddenInputProps() {
      return {
        name: name,
        value: internalValue !== null && internalValue !== void 0 ? internalValue : ''
      };
    },
    getClearButtonProps: function getClearButtonProps() {
      return {
        'aria-label': 'Clear selection',
        onClick: function onClick() {
          setInternalValue(null);
          openMenu();
        }
      };
    }
  });
};

var Dropdown = function Dropdown(props, ref) {
  var _splitSystemProps = splitSystemProps(props),
    systemProps = _splitSystemProps.systemProps,
    comboboxProps = _splitSystemProps.props;
  var items = comboboxProps.items,
    iconBefore = comboboxProps.iconBefore;
  var dropdownProps = useDropdown(comboboxProps);
  var getHiddenInputProps = dropdownProps.getHiddenInputProps,
    inPortal = dropdownProps.inPortal,
    isOpen = dropdownProps.isOpen,
    isInvalid = dropdownProps.isInvalid,
    getContainerProps = dropdownProps.getContainerProps;
  var styles = useMultiStyleConfig('Dropdown', _objectSpread2(_objectSpread2({}, props), {}, {
    isInvalid: isInvalid
  }));
  return jsx(DropdownProvider, {
    value: dropdownProps,
    children: jsx(DropdownStylesProvider, {
      value: styles,
      children: jsxs(system.div, _objectSpread2(_objectSpread2(_objectSpread2({
        className: "hover-dropdown"
      }, systemProps), getContainerProps()), {}, {
        __css: styles.container,
        children: [jsx("input", _objectSpread2({
          ref: ref,
          type: "hidden"
        }, getHiddenInputProps())), jsxs(Popover, {
          autoFocus: false,
          isOpen: isOpen,
          matchWidth: true,
          children: [jsx(DropdownInputSingle, {
            iconBefore: iconBefore
          }), inPortal ? jsx(Portal, {
            children: jsx(DropdownList, {
              items: items
            })
          }) : jsx(DropdownList, {
            items: items
          }), !isOpen && inPortal &&
          // When we're rendering the dropdown list in a portal, we need to
          // always render the list for accessibilty reasons (and to
          // prevent Downshift from warning about not calling
          // `getMenuProps`... to ensure proper accessibility)
          jsx(VisuallyHidden, {
            children: jsx(DropdownList, {
              items: items
            })
          })]
        })]
      }))
    })
  });
};
Dropdown.displayName = 'Dropdown';
var DropdownWithRef = /*#__PURE__*/forwardRef$1(Dropdown);

export { DropdownWithRef as Dropdown };
